import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Build, History, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DynamicContainer } from './common/DynamicContainer';
import McpExecutionLogDrawer from './McpExecutionLogDrawer';
import { fetchMcpCatalogEndpoints } from './common/mcpEndpointCatalogConfig';

const apiTenantToolsBase = (tenantId) =>
    `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/')}asrosync/agents/tenant/${encodeURIComponent(tenantId)}/tenant-tools`;
const API_GLOBAL_TOOLS = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/')}asrosync/admin/global-tools`;

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
    };
};

const isCdxUser = () => {
    try {
        return localStorage.getItem('cdx_user') === 'true';
    } catch {
        return false;
    }
};

const getDefaultTenantId = () => {
    const tid = localStorage.getItem('tenantId');
    if (!tid || tid === 'ALL' || tid === 'null' || tid === 'undefined') return '';
    return tid;
};

const TenantToolEntitlementsAdmin = () => {
    const [tenantId, setTenantId] = useState(getDefaultTenantId);
    const [tenants, setTenants] = useState([]);
    const [rows, setRows] = useState([]);
    const [globalTools, setGlobalTools] = useState([]);
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [logDrawerOpen, setLogDrawerOpen] = useState(false);
    const [logDrawerRow, setLogDrawerRow] = useState(null);
    const [opsPickerOpen, setOpsPickerOpen] = useState(false);
    const [opsPickerTool, setOpsPickerTool] = useState(null);
    const [opsPickerEndpoints, setOpsPickerEndpoints] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [pickGlobalToolIds, setPickGlobalToolIds] = useState([]);
    const [toolPickerSearch, setToolPickerSearch] = useState('');
    const [createdBy, setCreatedBy] = useState('tenant_tools_ui');
    const [savingAdd, setSavingAdd] = useState(false);

    const filteredRows = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((row) => (row.toolName || '').toLowerCase().includes(q));
    }, [rows, searchQuery]);

    const availableForCreate = useMemo(() => {
        const used = new Set(rows.map((r) => String(r.globalToolId)));
        return globalTools.filter((g) => g.toolId != null && !used.has(String(g.toolId)));
    }, [globalTools, rows]);

    const filteredAvailableForCreate = useMemo(() => {
        const q = toolPickerSearch.trim().toLowerCase();
        const selected = new Set((pickGlobalToolIds || []).map(String));
        return availableForCreate.filter((g) => {
            const id = String(g.toolId);
            // Keep checked items visible even when they don't match the search
            if (selected.has(id)) return true;
            if (!q) return true;
            const hay = [g.toolName, g.toolUniqueId, g.toolType, id]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [availableForCreate, toolPickerSearch, pickGlobalToolIds]);

    const openExecutionHistory = useCallback(
        async (row) => {
            if (!tenantId) {
                toast.warning('Select a tenant first');
                return;
            }
            const globalToolId = row?.globalToolId;
            if (globalToolId == null) {
                toast.info('This row has no linked global tool');
                return;
            }
            setHistoryLoading(true);
            try {
                const endpoints = await fetchMcpCatalogEndpoints({ globalToolId });
                const active = endpoints.filter((e) => e && e.active !== false);
                if (active.length === 0) {
                    toast.info('No MCP operations are defined for this tool in Global Tools');
                    return;
                }
                if (active.length === 1) {
                    setLogDrawerRow({
                        ...active[0],
                        tool_name: row.toolName || row.toolUniqueId || active[0].mec_operation_key,
                    });
                    setLogDrawerOpen(true);
                    return;
                }
                setOpsPickerTool(row);
                setOpsPickerEndpoints(active);
                setOpsPickerOpen(true);
            } catch (e) {
                console.error(e);
                toast.error('Could not load MCP operations for this tool');
            } finally {
                setHistoryLoading(false);
            }
        },
        [tenantId]
    );

    const columns = useMemo(
        () => [
            {
                field: 'execution_history',
                headerName: '',
                width: 48,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => (
                    <Tooltip title="MCP execution history and steps">
                        <span>
                            <IconButton
                                size="small"
                                color="primary"
                                disabled={historyLoading || !tenantId}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openExecutionHistory(params.row);
                                }}
                                aria-label="View MCP execution history"
                            >
                                <History fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
            },
            { field: 'toolName', headerName: 'Tool name', width: 200 },
            { field: 'agentPlatform', headerName: 'Platform', width: 110 },
            { field: 'toolType', headerName: 'Type', width: 140 },
            { field: 'toolUniqueId', headerName: 'Unique ID', width: 180 },
            {
                field: 'isEnabled',
                headerName: 'Enabled',
                width: 90,
                valueGetter: (_value, row) => (row?.isEnabled !== false ? 'Yes' : 'No'),
            },
        ],
        [historyLoading, tenantId, openExecutionHistory]
    );

    const fetchTenants = useCallback(async () => {
        if (!isCdxUser()) return;
        try {
            const res = await fetch(
                `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/')}synctrack/tenants`,
                { headers: getAuthHeaders() }
            );
            if (!res.ok) return;
            const data = await res.json();
            setTenants(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
        }
    }, []);

    const fetchGlobalTools = useCallback(async () => {
        try {
            const res = await fetch(API_GLOBAL_TOOLS, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setGlobalTools(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            toast.error(`Error fetching global tools: ${e.message}`);
        }
    }, []);

    const fetchRows = useCallback(async (tid) => {
        if (!tid) {
            setRows([]);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(apiTenantToolsBase(tid), {
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setRows(Array.isArray(data.rows) ? data.rows : []);
        } catch (e) {
            console.error(e);
            toast.error(`Error fetching tenant tools: ${e.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTenants();
        fetchGlobalTools();
    }, [fetchTenants, fetchGlobalTools]);

    useEffect(() => {
        const onTenantChange = (event) => {
            const next = event?.detail?.tenantId;
            if (next && next !== 'ALL') {
                setTenantId(String(next));
            }
        };
        window.addEventListener('tenantChange', onTenantChange);
        return () => window.removeEventListener('tenantChange', onTenantChange);
    }, []);

    useEffect(() => {
        setSearchQuery('');
        fetchRows(tenantId);
    }, [tenantId, fetchRows]);

    const openAddDialog = () => {
        if (!tenantId) {
            toast.warning('Select a tenant first');
            return;
        }
        setPickGlobalToolIds([]);
        setToolPickerSearch('');
        setCreatedBy('tenant_tools_ui');
        setAddDialogOpen(true);
    };

    const closeAddDialog = () => {
        if (savingAdd) return;
        setAddDialogOpen(false);
        setPickGlobalToolIds([]);
        setToolPickerSearch('');
    };

    const handleAddSelectedTools = async () => {
        if (!tenantId) {
            toast.error('Select a tenant first');
            return;
        }
        const selectedIds = [...new Set((pickGlobalToolIds || []).map(String).filter(Boolean))];
        if (selectedIds.length === 0) {
            toast.error('Select at least one global tool');
            return;
        }
        const alreadyOnTenant = new Set(rows.map((r) => String(r.globalToolId)).filter(Boolean));
        const toAssign = selectedIds
            .filter((id) => !alreadyOnTenant.has(id))
            .map((id) => availableForCreate.find((g) => String(g.toolId) === id))
            .filter(Boolean);

        if (toAssign.length === 0) {
            toast.info('Selected tools are already assigned to this tenant');
            closeAddDialog();
            return;
        }

        setSavingAdd(true);
        const okNames = [];
        const failMessages = [];
        try {
            for (const tool of toAssign) {
                try {
                    const res = await fetch(apiTenantToolsBase(tenantId), {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        body: JSON.stringify({
                            global_tool_id: tool.toolId,
                            created_by: createdBy || 'tenant_tools_ui',
                        }),
                    });
                    const payload = await res.json().catch(() => ({}));
                    if (!res.ok) {
                        const msg = payload.error || payload.message || `HTTP ${res.status}`;
                        if (/already|duplicate|exists/i.test(String(msg))) {
                            continue;
                        }
                        failMessages.push(`${tool.toolName || tool.toolUniqueId}: ${msg}`);
                        continue;
                    }
                    okNames.push(tool.toolName || tool.toolUniqueId || String(tool.toolId));
                } catch (e) {
                    failMessages.push(
                        `${tool.toolName || tool.toolUniqueId || tool.toolId}: ${e.message || 'Failed'}`
                    );
                }
            }

            if (okNames.length > 0) {
                toast.success(
                    okNames.length === 1
                        ? `${okNames[0]} assigned to tenant`
                        : `${okNames.length} tools assigned to tenant`
                );
            }
            if (failMessages.length > 0) {
                toast.error(
                    failMessages.length === 1
                        ? failMessages[0]
                        : `${failMessages.length} tool(s) failed to assign`
                );
            }
            if (okNames.length === 0 && failMessages.length === 0) {
                toast.info('Selected tools are already assigned to this tenant');
            }

            await fetchRows(tenantId);
            setAddDialogOpen(false);
            setPickGlobalToolIds([]);
            setToolPickerSearch('');
        } finally {
            setSavingAdd(false);
        }
    };

    const handleDelete = async (id) => {
        if (!tenantId) throw new Error('Select a tenant first');
        const res = await fetch(
            `${apiTenantToolsBase(tenantId)}/${encodeURIComponent(id)}`,
            { method: 'DELETE', headers: getAuthHeaders() }
        );
        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
        }
        toast.success('Tenant tool entitlement removed successfully');
        await fetchRows(tenantId);
        return true;
    };

    const tenantSelector = (
        <FormControl size="small" sx={{ minWidth: 200, mr: 1 }}>
            <InputLabel id="tat-tenant-select-label">Tenant</InputLabel>
            <Select
                labelId="tat-tenant-select-label"
                label="Tenant"
                value={tenantId || ''}
                onChange={(e) => setTenantId(String(e.target.value))}
            >
                {tenants.map((t) => (
                    <MenuItem key={t.tenantId} value={String(t.tenantId)}>
                        {t.tenantName || t.tenantId}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    return (
        <Box>
            <Alert severity="info" sx={{ m: 1 }}>
                Assign <strong>global tools</strong> to each tenant (stored in <strong>tenant_tool</strong>, same pattern
                as Master Connector → Sync Connector). MCP endpoints for each tool are enabled automatically. Use the{' '}
                <strong>History</strong> icon to view MCP run logs and steps. In <strong>Agent Setup</strong>, users
                connect Sync Connectors for tools whose <strong>Agent platform</strong> matches the agent (langchain / n8n).
            </Alert>
            {!tenantId && (
                <Alert severity="warning" sx={{ mx: 1, mb: 1 }}>
                    Select a tenant to manage tenant tools.
                </Alert>
            )}
            <DynamicContainer
                title="Tenant Tools"
                icon={Build}
                columns={columns}
                formFields={[]}
                initialFormData={{}}
                idField="id"
                rows={filteredRows}
                onAddClick={openAddDialog}
                onDelete={handleDelete}
                isLoading={loading}
                isCdxUser={isCdxUser()}
                functionCode="Agent Tools"
                hideAdd={!tenantId}
                hideEdit
                headerExtra={isCdxUser() && tenants.length > 0 ? tenantSelector : null}
                subHeader={
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            px: 2,
                            py: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search by tool name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            disabled={!tenantId || loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ minWidth: 280 }}
                        />
                    </Box>
                }
            />

            <Dialog open={addDialogOpen} onClose={closeAddDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'background.paper' }}>
                    Add Tenant Tools
                </DialogTitle>
                <DialogContent>
                    {availableForCreate.length === 0 ? (
                        <Typography color="text.secondary" sx={{ pt: 2 }}>
                            No global tools available to add. All tools are already assigned to this tenant, or none
                            exist yet.
                        </Typography>
                    ) : (
                        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search global tools..."
                                value={toolPickerSearch}
                                onChange={(e) => setToolPickerSearch(e.target.value)}
                                disabled={savingAdd}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormControl fullWidth>
                                <InputLabel id="add-tenant-tools-label">Global tool *</InputLabel>
                                <Select
                                    labelId="add-tenant-tools-label"
                                    multiple
                                    value={pickGlobalToolIds}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setPickGlobalToolIds(
                                            typeof v === 'string' ? v.split(',') : v.map(String)
                                        );
                                    }}
                                    input={<OutlinedInput label="Global tool *" />}
                                    renderValue={(selected) => {
                                        const labels = (selected || []).map((id) => {
                                            const t = availableForCreate.find(
                                                (o) => String(o.toolId) === String(id)
                                            );
                                            return t
                                                ? `${t.toolName || t.toolUniqueId}${
                                                      t.toolType ? ` (${t.toolType})` : ''
                                                  }`
                                                : String(id);
                                        });
                                        return labels.join(', ');
                                    }}
                                    MenuProps={{
                                        PaperProps: { style: { maxHeight: 360 } },
                                        // Keep menu open while toggling checkboxes
                                        autoFocus: false,
                                    }}
                                >
                                    {filteredAvailableForCreate.length === 0 ? (
                                        <MenuItem disabled>
                                            <ListItemText primary="No tools match your search" />
                                        </MenuItem>
                                    ) : (
                                        filteredAvailableForCreate.map((t) => {
                                            const id = String(t.toolId);
                                            const name = t.toolName || t.toolUniqueId;
                                            return (
                                                <MenuItem
                                                    key={id}
                                                    value={id}
                                                    dense
                                                    sx={{ py: 0.25, minHeight: 28 }}
                                                >
                                                    <Checkbox
                                                        checked={pickGlobalToolIds.includes(id)}
                                                        size="small"
                                                        sx={{ py: 0, pl: 0.5, pr: 0.75 }}
                                                    />
                                                    <ListItemText
                                                        primary={
                                                            t.toolType ? `${name} (${t.toolType})` : name
                                                        }
                                                        primaryTypographyProps={{
                                                            variant: 'body2',
                                                            noWrap: true,
                                                        }}
                                                        sx={{ my: 0 }}
                                                    />
                                                </MenuItem>
                                            );
                                        })
                                    )}
                                </Select>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 1, display: 'block' }}
                                >
                                    Check one or more tools, then Save. Already-assigned tools are not listed.
                                </Typography>
                            </FormControl>
                            <TextField
                                label="Created by"
                                value={createdBy}
                                onChange={(e) => setCreatedBy(e.target.value)}
                                fullWidth
                                disabled={savingAdd}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAddDialog} disabled={savingAdd}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddSelectedTools}
                        disabled={
                            savingAdd ||
                            pickGlobalToolIds.length === 0 ||
                            availableForCreate.length === 0
                        }
                    >
                        {savingAdd
                            ? 'Saving…'
                            : pickGlobalToolIds.length > 1
                              ? `Save ${pickGlobalToolIds.length}`
                              : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={opsPickerOpen}
                onClose={() => {
                    setOpsPickerOpen(false);
                    setOpsPickerTool(null);
                    setOpsPickerEndpoints([]);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    MCP operations — {opsPickerTool?.toolName || 'Tool'}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Choose an operation to view execution history for this tenant.
                    </Typography>
                    <List dense disablePadding>
                        {opsPickerEndpoints.map((ep) => (
                            <ListItemButton
                                key={ep.mec_id}
                                onClick={() => {
                                    setOpsPickerOpen(false);
                                    const tool = opsPickerTool;
                                    setOpsPickerTool(null);
                                    setOpsPickerEndpoints([]);
                                    setLogDrawerRow({
                                        ...ep,
                                        tool_name:
                                            tool?.toolName ||
                                            tool?.toolUniqueId ||
                                            ep.mec_operation_key,
                                    });
                                    setLogDrawerOpen(true);
                                }}
                            >
                                <ListItemText
                                    primary={ep.mec_operation_key || ep.mec_endpoint_key || 'Operation'}
                                    secondary={[ep.mec_method, ep.mec_path].filter(Boolean).join(' ')}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </DialogContent>
            </Dialog>

            <McpExecutionLogDrawer
                open={logDrawerOpen}
                onClose={() => {
                    setLogDrawerOpen(false);
                    setLogDrawerRow(null);
                }}
                tenantId={tenantId}
                endpointRow={logDrawerRow}
            />
        </Box>
    );
};

export default TenantToolEntitlementsAdmin;
